type ToolInput = Record<string, any>;

declare let window: any;

/**
 * Resolves a kuikaId to a human-readable label like "Panel1" or "Col [5]".
 * Falls back to "#kuikaId" if the dictionary is unavailable.
 */
function resolveName(kuikaId: string | number | undefined): string {
  if (kuikaId == null || kuikaId === "") return "";
  const id = String(kuikaId);
  if (id === "-1") return "Body";
  if (id === "-2") return "Header";
  if (id === "-3") return "Footer";
  try {
    const sd = window.getSdInstance?.();
    const dict = sd?.screenProvider?.state?.screenDesignDictionary || sd?.state?.screenDesignDictionary;
    const di = dict?.[id];
    if (di) {
      return di.friendlyName || di.item?.name || `#${id}`;
    }
  } catch {
    // Designer not ready — fall back
  }
  // Numeric ids keep a debuggable "#id" fallback; synthetic build-time markup
  // refs (e.g. "@_c4") are not real components — return empty so callers emit a
  // generic label instead of leaking "#@_c4" into the UI.
  return /^-?\d+$/.test(id) ? `#${id}` : "";
}

/** Short label: friendlyName or componentName, with target context */
function addLabel(componentName: string, friendlyName?: string, targetId?: string): string {
  const name = friendlyName ? `'${friendlyName}'` : componentName;
  const target = targetId != null ? resolveName(targetId) : "";
  return target ? `${name} → ${target}` : name;
}

const TOOL_DESCRIPTIONS: Record<string, (input: ToolInput) => string> = {
  // ── Category tools (dispatch by operation) ──────────────────────────

  ui_screen_query: (i) => {
    switch (i.operation) {
      case "get_tree":
        return "Reading screen structure";
      case "get_info":
        return `Inspecting ${resolveName(i.kuikaId) || "component"}`;
      case "get_selected":
        return "Checking selection";
      case "list_available":
        return "Listing components";
      case "get_catalog":
        if (i.componentName) return `Looking up ${i.componentName}`;
        if (i.category) return `Browsing ${i.category}`;
        return "Browsing catalog";
      case "get_children":
        return `Reading children of ${resolveName(i.kuikaId) || "component"}`;
      case "get_events":
        return `Reading events of ${resolveName(i.kuikaId) || "component"}`;
      case "get_styles":
        return `Reading styles of ${resolveName(i.kuikaId) || "component"}`;
      case "get_multiple_styles": {
        const ids = Array.isArray(i.kuikaIds) ? i.kuikaIds : [];
        if (ids.length === 0) return "Reading multiple styles";
        const names = ids.slice(0, 3).map((id: string) => resolveName(id));
        const suffix = ids.length > 3 ? ` +${ids.length - 3} more` : "";
        return `Reading styles of ${names.join(", ")}${suffix}`;
      }
      case "get_hierarchy":
        return `Reading hierarchy of ${resolveName(i.kuikaId) || "component"}`;
      case "find_by_name":
        return i.componentName ? `Finding '${i.componentName}'` : "Finding components";
      case "find_by_type":
        return i.componentType ? `Finding all ${i.componentType} components` : "Finding components by type";
      case "list_all_components":
        return "Listing all components";
      case "search":
        return i.query ? `Searching for '${i.query}'` : "Searching components";
      case "get_property_value":
        return `Reading properties of ${resolveName(i.kuikaId) || "component"}`;
      case "check_completeness":
        return "Checking screen completeness";
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_component: (i) => {
    switch (i.operation) {
      case "add": {
        const items = Array.isArray(i.components) ? i.components : [];
        if (items.length === 0) return "Adding components";
        if (items.length === 1) {
          const c = items[0];
          return `Adding ${addLabel(c.componentName || "component", c.friendlyName, c.targetKuikaId)}`;
        }
        const names = items.slice(0, 3).map((c: any) => c.friendlyName || c.componentName || "component");
        const suffix = items.length > 3 ? ` +${items.length - 3} more` : "";
        return `Adding ${names.join(", ")}${suffix}`;
      }
      case "configure": {
        const items = Array.isArray(i.components) ? i.components : [];
        if (items.length === 0) return "Configuring components";
        if (items.length === 1) {
          const c = items[0];
          const name = resolveName(c.kuikaId);
          const count = Array.isArray(c.properties) ? c.properties.length : 0;
          return name ? `Setting ${count || "multiple"} props on ${name}` : `Setting ${count || "multiple"} properties`;
        }
        const names = items.map((c: any) => resolveName(c.kuikaId)).filter(Boolean);
        if (names.length === 0) return "Configuring components";
        const shown = names.slice(0, 3);
        const suffix = names.length > 3 ? ` +${names.length - 3} more` : "";
        return `Configuring ${shown.join(", ")}${suffix}`;
      }
      case "delete": {
        const delIds = Array.isArray(i.kuikaIds) ? i.kuikaIds : i.kuikaId ? [i.kuikaId] : [];
        if (delIds.length === 1) return `Deleting ${resolveName(delIds[0]) || "component"}`;
        if (delIds.length > 1) {
          const delNames = delIds
            .slice(0, 3)
            .map((id: string | number) => resolveName(id))
            .filter(Boolean);
          const delSuffix = delIds.length > 3 ? ` +${delIds.length - 3} more` : "";
          return delNames.length > 0
            ? `Deleting ${delNames.join(", ")}${delSuffix}`
            : `Deleting ${delIds.length} components`;
        }
        return "Deleting component";
      }
      case "select":
        return `Selecting ${resolveName(i.kuikaId) || "component"}`;
      case "move": {
        const src = resolveName(i.kuikaId);
        const dst = resolveName(i.targetParentKuikaId);
        return src && dst ? `Moving ${src} → ${dst}` : "Moving component";
      }
      case "clone": {
        const src = resolveName(i.kuikaId);
        const dst = i.targetParentKuikaId ? resolveName(i.targetParentKuikaId) : null;
        return dst ? `Cloning ${src || "component"} → ${dst}` : `Cloning ${src || "component"}`;
      }
      case "reorder": {
        const name = resolveName(i.kuikaId);
        return i.direction ? `Reordering ${name || "component"} ${i.direction}` : `Reordering ${name || "component"}`;
      }
      case "swap": {
        const a = resolveName(i.firstKuikaId);
        const b = resolveName(i.secondKuikaId);
        return a && b ? `Swapping ${a} ↔ ${b}` : "Swapping components";
      }
      case "rename": {
        const name = resolveName(i.kuikaId);
        return i.newName ? `Renaming ${name || "component"} → '${i.newName}'` : "Renaming component";
      }
      case "set_responsive_columns":
        return "Setting responsive columns";
      case "resize_column":
        return "Resizing column";
      case "get_visibility":
        return `Reading visibility of ${resolveName(i.kuikaId) || "component"}`;
      case "set_visibility":
        return `Setting visibility of ${resolveName(i.kuikaId) || "component"}`;
      case "get_editability":
        return `Reading editability of ${resolveName(i.kuikaId) || "component"}`;
      case "set_editability":
        return `Setting editability of ${resolveName(i.kuikaId) || "component"}`;
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_style: (i) => {
    switch (i.operation) {
      case "set": {
        const items = Array.isArray(i.components) ? i.components : [];
        if (items.length > 0) {
          const names = items.slice(0, 3).map((c: any) => resolveName(c.kuikaId));
          const suffix = items.length > 3 ? ` +${items.length - 3} more` : "";
          return `Styling ${names.join(", ")}${suffix}`;
        }
        return `Styling ${resolveName(i.kuikaId) || "component"}`;
      }
      case "get_definition":
        return "Reading component definition";
      case "get_defaults":
        return "Reading default styles";
      case "review":
        return "Reviewing screen design";
      case "apply_fixes": {
        const count = Array.isArray(i.fixes) ? i.fixes.length : 0;
        return `Applying ${count || "multiple"} design fixes`;
      }
      case "screenshot":
        if (i.kuikaId) return `Capturing screenshot of ${resolveName(i.kuikaId)}`;
        return "Capturing full screen screenshot";
      case "list_colors":
        return "Listing project colors";
      case "manage_color":
        switch (i.colorAction) {
          case "add":
            return i.name ? `Adding color '${i.name}'` : "Adding project color";
          case "update":
            return "Updating project color";
          case "delete":
            return "Deleting project color";
          default:
            return "Managing project color";
        }
      case "list_unified":
        return "Listing style tokens";
      case "apply_unified":
        return i.name ? `Applying style token '${i.name}'` : "Applying style token";
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_property: (i) => {
    switch (i.operation) {
      case "list_binding_options":
        return "Listing binding options";
      case "set_binding":
        return "Setting property binding";
      case "set_symbol":
        return "Setting property symbol";
      case "update_symbol":
        return "Updating property symbol";
      case "set_type":
        return "Setting property type";
      case "set_type_options":
        return "Setting type options";
      case "get_data_editor":
        return "Reading data editor";
      case "set_data_action":
        return "Setting datasource action";
      case "set_data_field":
        return "Setting data field";
      case "get_dynamic_types":
        return "Reading dynamic types";
      case "set_value_binding":
        return `Binding ${resolveName(i.kuikaId) || "component"} value`;
      case "set_multiple_data_fields":
        return `Wiring data fields on ${resolveName(i.kuikaId) || "component"}`;
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_action: (i) => {
    switch (i.operation) {
      case "get_parameter_options":
        return "Reading action parameters";
      case "get_parameter_editor_options":
        return "Reading parameter editor";
      case "list_parameter_binding_options":
        return "Listing parameter bindings";
      case "set_parameter_binding":
        return "Setting parameter binding";
      case "list_screen_actions":
        return "Listing actions";
      case "list_current_actions":
        return "Listing current actions";
      case "get_details":
        return "Reading action details";
      case "add_to_event": {
        const name = i.actionDefinitionId || "action";
        return i.eventName ? `Adding ${name} to ${i.eventName}` : "Adding action to event";
      }
      case "add_multiple_to_event": {
        const count = Array.isArray(i.actions) ? i.actions.length : "multiple";
        return `Adding ${count} actions to event`;
      }
      case "add_to_branch":
        return `Adding action to ${i.branch || "if"} branch`;
      case "delete_from_event":
        return "Removing action";
      case "delete_from_branch":
        return "Removing action from branch";
      case "update_parameters":
        return "Updating action parameters";
      case "update_properties":
        return "Updating action properties";
      case "reorder":
        return "Reordering actions";
      case "reorder_branch":
        return "Reordering branch actions";
      case "create_conditional":
        return "Creating conditional action";
      case "add_condition":
        return "Adding condition";
      case "set_condition":
        return "Setting action condition";
      case "remove_condition":
        return "Removing condition";
      case "add_confirmation":
        return "Adding confirmation";
      case "remove_confirmation":
        return "Removing confirmation";
      case "export_clipboard":
        return "Exporting action chain";
      case "paste_clipboard":
        return "Pasting action chain";
      case "set_managed_db_data":
        return "Setting managed DB data";
      case "set_multiple_parameter_bindings": {
        const count = Array.isArray(i.bindings) ? i.bindings.length : 0;
        return count ? `Binding ${count} action parameters` : "Binding action parameters";
      }
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_action_extended: (i) => {
    switch (i.operation) {
      case "add_to_for_each":
        return "Adding to forEach";
      case "set_for_each_data":
        return "Setting forEach data";
      case "reorder_for_each":
        return "Reordering forEach";
      case "add_to_mqtt":
        return "Adding to MQTT";
      case "set_mqtt_data":
        return "Setting MQTT data";
      case "reorder_mqtt":
        return "Reordering MQTT";
      case "add_recurring":
        return "Adding recurring schedule";
      case "remove_recurring":
        return "Removing recurring";
      case "set_recurring_data":
        return "Setting recurring data";
      case "add_to_recurring":
        return "Adding to recurring";
      case "reorder_recurring":
        return "Reordering recurring";
      default:
        return `Action ext: ${i.operation || "unknown"}`;
    }
  },

  ui_screen: (i) => {
    switch (i.operation) {
      case "list":
        return "Listing screens";
      case "get_current":
        return "Checking current screen";
      case "navigate":
        return "Navigating to screen";
      case "create":
        return i.screenName ? `Creating '${i.screenName}' screen` : "Creating screen";
      case "delete":
        return "Deleting screen";
      case "rename":
        return i.newName ? `Renaming screen to '${i.newName}'` : "Renaming screen";
      case "duplicate":
        return "Duplicating screen";
      case "create_folder":
        return i.folderName ? `Creating '${i.folderName}' folder` : "Creating folder";
      case "move_to_folder":
        return "Moving screen to folder";
      case "get_press_enter_button":
        return "Reading Enter-key button";
      case "set_press_enter_button":
        return "Setting Enter-key button";
      case "list_shortcuts":
        return "Listing keyboard shortcuts";
      case "add_shortcut":
        return i.keyCombo ? `Adding shortcut '${i.keyCombo}'` : "Adding keyboard shortcut";
      case "update_shortcut":
        return "Updating keyboard shortcut";
      case "delete_shortcut":
        return "Removing keyboard shortcut";
      case "set_offline":
        return "Setting offline mode";
      case "set_pull_to_refresh":
        return "Setting pull-to-refresh";
      case "create_master":
        return i.screenName ? `Creating master screen '${i.screenName}'` : "Creating master screen";
      case "set_master_screen":
        return "Assigning master screen";
      case "get_layout_positions":
        return "Reading layout positions";
      case "set_layout_positions":
        return "Setting layout positions";
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_project: (i) => {
    switch (i.operation) {
      case "save":
        return "Saving current screen";
      case "reload_screen":
        return "Reloading screen";
      case "reload_datasources":
        return "Reloading datasources";
      case "reload_project":
        return "Refreshing project data";
      case "preview":
        return "Starting project preview";
      case "get_current_view":
        return "Checking current view";
      case "navigate_to_view":
        return i.view ? `Switching to ${i.view}` : "Switching view";
      case "set_left_panel":
        return i.panel ? `Opening ${i.panel} panel` : "Switching panel";
      case "set_right_panel":
        return i.panel ? `Opening ${i.panel} panel` : "Switching panel";
      case "list_datasources":
        return "Listing datasources";
      case "get_datasource_actions":
        return "Reading datasource actions";
      case "navigate_to_datasource":
        return "Opening datasource";
      case "list_devices":
        return "Listing devices";
      case "get_current_device":
        return "Checking current device";
      case "set_device":
        return "Switching viewport";
      case "export_translations":
        return "Exporting translations";
      case "export_excel_template":
        return "Exporting Excel template";
      case "query_datasource_actions": {
        // Tool docs use query:"." as "list ALL project actions" (regex any-char).
        // Showing "for '.'" in the chat is nonsense to users — treat list-all
        // sentinels as a full inventory scan, not a keyword search.
        const rawQuery: unknown = i.query;
        const query: string = typeof rawQuery === "string" ? rawQuery.trim() : "";
        if (query.length === 0) {
          return "Searching datasource actions";
        }
        if (query === "." || query === ".*" || query === "^" || query === ".*?" || query === "\\w*") {
          return "Listing all project datasource actions";
        }
        return `Searching datasource actions for '${query}'`;
      }
      case "list_roles":
        return "Listing roles";
      case "create_role":
        return i.name ? `Creating role '${i.name}'` : "Creating role";
      case "update_role":
        return "Updating role";
      case "delete_role":
        return "Deleting role";
      case "set_screen_role":
        return i.screenRole ? `Setting ${i.screenRole} screen role` : "Setting screen role";
      case "get_screen_authorization":
        return "Reading authorization";
      case "set_screen_authorization":
        return "Setting authorization";
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_project_extended: (i) => {
    switch (i.operation) {
      case "create_folder":
        return i.folderName ? `Creating '${i.folderName}' folder` : "Creating folder";
      case "rename_folder":
        return "Renaming folder";
      case "delete_folder":
        return `Deleting ${i.folderType || ""} folder`;
      case "add_item_to_folder":
        return "Moving item to folder";
      case "remove_item_from_folder":
        return "Removing item from folder";
      case "create_subfolder":
        return "Creating subfolder";
      case "rename_subfolder":
        return "Renaming subfolder";
      case "delete_subfolder":
        return "Deleting subfolder";
      case "add_item_to_subfolder":
        return "Moving item to subfolder";
      case "remove_item_from_subfolder":
        return "Removing from subfolder";
      case "list_comments":
        return "Listing comments";
      case "post_comment":
        return "Posting comment";
      case "reply_to_comment":
        return "Replying to comment";
      case "list_workflows":
        return "Listing workflows";
      case "navigate_to_workflow":
        return "Opening workflow";
      case "list_scheduled_jobs":
        return "Listing scheduled jobs";
      case "navigate_to_scheduled_job":
        return "Opening scheduled job";
      case "list_apis":
        return "Listing APIs";
      case "get_api_details":
        return "Reading API details";
      case "navigate_to_api":
        return "Opening API editor";
      case "list_images":
        return "Listing images";
      case "upload_image":
        return "Uploading image";
      case "list_roles":
        return "Listing roles";
      case "create_role":
        return i.name ? `Creating '${i.name}' role` : "Creating role";
      case "update_role":
        return "Updating role";
      case "delete_role":
        return "Deleting role";
      case "get_screen_authorization":
        return "Reading selection authorization";
      case "set_screen_authorization":
        return "Setting selection authorization";
      case "list_folders":
        return "Listing folders";
      case "create_workflow":
        return i.name ? `Creating workflow '${i.name}'` : "Creating workflow";
      case "delete_workflow":
        return "Deleting workflow";
      case "duplicate_workflow":
        return "Duplicating workflow";
      default:
        return humanizeOperation(i.operation);
    }
  },

  ui_screen_input: (i) => {
    switch (i.operation) {
      case "list":
        return "Listing screen inputs";
      case "create":
        return i.name ? `Creating screen input '${i.name}'` : "Creating screen input";
      case "update":
        return "Updating screen input";
      case "delete":
        return "Deleting screen input";
      default:
        return `Screen input: ${i.operation || "unknown"}`;
    }
  },

  ui_validation: (i) => {
    switch (i.operation) {
      case "list":
        return `Listing validations of ${resolveName(i.kuikaId) || "component"}`;
      case "add": {
        const name = resolveName(i.kuikaId);
        const type = i.validationType || "validation";
        return name ? `Adding ${type} validation to ${name}` : `Adding ${type} validation`;
      }
      case "update":
        return `Updating validation on ${resolveName(i.kuikaId) || "component"}`;
      case "remove":
        return `Removing validation from ${resolveName(i.kuikaId) || "component"}`;
      case "validate_screen":
        if (i.scope === "section" && i.kuikaId) return `Validating ${resolveName(i.kuikaId)}`;
        return "Validating screen";
      default:
        return `Validation: ${i.operation || "unknown"}`;
    }
  },

  ui_microapp: (i) => {
    switch (i.operation) {
      case "list_projects":
        return "Listing MicroApp projects";
      case "list_screens":
        return "Listing MicroApp screens";
      case "list_screen_inputs":
        return "Listing MicroApp inputs";
      case "set_project":
        return "Setting MicroApp project";
      case "set_starting_screen":
        return "Setting MicroApp screen";
      default:
        return `MicroApp: ${i.operation || "unknown"}`;
    }
  },

  ui_history: (i) => {
    switch (i.operation) {
      case "undo":
        return "Undoing last change";
      case "redo":
        return "Redoing change";
      default:
        return `History: ${i.operation || "unknown"}`;
    }
  },

  ui_workflow_dsl: () => "Designing workflow",

  // ── Backend grouped tools (dispatch by operation) ───────────────────

  ds_datasource: (i) => {
    switch (i.operation) {
      case "get_all":
        return "Listing datasources";
      case "get_detail":
        return "Reading datasource details";
      case "save_swagger":
        return i.swaggerUrl ? "Importing Swagger" : "Saving datasource";
      case "delete":
        return "Deleting datasource";
      default:
        return `Datasource: ${i.operation || "unknown"}`;
    }
  },

  ds_sap: (i) => {
    switch (i.operation) {
      case "test_connection":
        return "Testing SAP connection";
      case "save_datasource":
        return "Saving SAP datasource";
      case "search_functions":
        return "Searching SAP functions";
      case "get_function_signature":
        return "Reading SAP function signature";
      case "save_action":
        return "Saving SAP action";
      default:
        return `SAP: ${i.operation || "unknown"}`;
    }
  },

  ds_csharp: (i) => {
    switch (i.operation) {
      case "get_template":
        return "Getting C# template";
      case "save":
        return "Saving C# datasource";
      default:
        return `C# datasource: ${i.operation || "unknown"}`;
    }
  },

  ds_action: (i) => {
    switch (i.operation) {
      case "delete":
        return "Deleting action";
      default:
        return `Action: ${i.operation || "unknown"}`;
    }
  },

  database_schema: (i) => {
    switch (i.operation) {
      case "get_schema_name":
        return "Reading schema name";
      case "get_tables":
        return "Listing database tables";
      case "get_table_schema":
        return "Reading table schema";
      case "execute_migration":
        return "Executing migration";
      case "get_column_types":
        return "Reading column types";
      case "get_table_columns":
        return "Reading table columns";
      case "search_tables":
        return i.query ? `Searching tables for '${i.query}'` : "Searching tables";
      case "create_table":
        return i.tableName ? `Creating table '${i.tableName}'` : "Creating table";
      case "drop_table":
        return i.tableName ? `Dropping table '${i.tableName}'` : "Dropping table";
      case "rename_table":
        return "Renaming table";
      case "add_column":
        return i.columnName ? `Adding column '${i.columnName}'` : "Adding column";
      case "drop_column":
        return i.columnName ? `Dropping column '${i.columnName}'` : "Dropping column";
      case "rename_column":
        return "Renaming column";
      case "alter_column":
        return i.columnName ? `Updating column '${i.columnName}'` : "Updating column";
      default:
        return humanizeOperation(i.operation);
    }
  },

  db_sql_action: (i) => {
    switch (i.operation) {
      case "save_datasource":
        return "Saving database datasource";
      case "save_action":
        return "Saving SQL action";
      case "get_parameter_types":
        return "Reading parameter types";
      case "save_multiple": {
        const count = Array.isArray(i.actions) ? i.actions.length : 0;
        return count ? `Saving ${count} SQL actions` : "Saving SQL actions";
      }
      default:
        return humanizeOperation(i.operation);
    }
  },

  database_data: (i) => {
    switch (i.operation) {
      case "seed":
        return "Seeding demo data";
      case "list_seedable":
        return "Listing seedable tables";
      default:
        return humanizeOperation(i.operation);
    }
  },

  // CRUD tools
  crud_create_screen_by_table_name: (i) =>
    i.tableName ? `Creating CRUD screen for '${i.tableName}'` : "Creating CRUD screen",

  translation: (i) => {
    switch (i.operation) {
      case "get_supported_languages":
        return "Listing supported languages";
      case "add_language":
        return "Adding language";
      case "get_project_languages":
        return "Reading project languages";
      case "get_all":
        return "Listing translations";
      case "save":
        return "Saving translation";
      case "delete_all":
        return "Deleting all translations";
      case "delete_screen":
        return "Deleting screen translations";
      case "set_default":
        return "Setting default language";
      case "remove_language":
        return "Removing language";
      default:
        return `Translation: ${i.operation || "unknown"}`;
    }
  },

  scheduler_job: (i) => {
    switch (i.operation) {
      case "create":
        return "Creating scheduled job";
      case "update":
        return "Updating scheduled job";
      case "get_all":
        return "Listing scheduled jobs";
      case "get_detail":
        return "Reading job details";
      case "activate":
        return "Activating scheduled job";
      case "inactivate":
        return "Deactivating scheduled job";
      case "delete":
        return "Deleting scheduled job";
      default:
        return `Scheduler: ${i.operation || "unknown"}`;
    }
  },

  api_management: (i) => {
    switch (i.operation) {
      case "create_controller":
        return "Creating API controller";
      case "rename_controller":
        return "Renaming API controller";
      case "get_all_controllers":
        return "Listing API controllers";
      case "get_apis":
        return "Reading controller APIs";
      case "get_api_detail":
        return "Reading API details";
      case "find_api_by_name":
        return "Searching APIs by name";
      case "create_api":
        return "Creating API endpoint";
      case "update_api":
        return "Updating API endpoint";
      case "add_api_parameter":
        return "Adding API parameter";
      case "remove_api_parameter":
        return "Removing API parameter";
      case "add_action_to_api":
        return "Linking action to API";
      case "remove_action_from_api":
        return "Unlinking action from API";
      case "set_api_authorization":
        return "Setting API authorization";
      case "save_api":
        return "Saving API";
      case "remove_api":
        return "Removing API";
      case "delete_controller":
        return "Deleting controller";
      default:
        return `API management: ${i.operation || "unknown"}`;
    }
  },

  package_management: (i) => {
    switch (i.operation) {
      case "get_available":
        return "Listing available packages";
      case "search_nuget":
        return "Searching NuGet packages";
      case "get_nuget_details":
        return "Reading NuGet package details";
      case "get_libraries":
        return "Listing project libraries";
      case "get_library_detail":
        return "Reading library details";
      case "install":
        return "Installing NuGet library";
      case "update":
        return "Updating library";
      case "uninstall":
        return "Uninstalling library";
      case "activate":
        return "Activating library";
      case "deactivate":
        return "Deactivating library";
      default:
        return `Package: ${i.operation || "unknown"}`;
    }
  },

  lsp_csharp: (i) => {
    switch (i.operation) {
      case "get_diagnostics":
        return "Running C# diagnostics";
      case "format_code":
        return "Formatting C# code";
      default:
        return `LSP C#: ${i.operation || "unknown"}`;
    }
  },

  offline_table: (i) => {
    switch (i.operation) {
      case "get_all":
        return "Listing offline tables";
      case "get_detail":
        return "Reading offline table";
      case "save":
        return "Updating offline table";
      case "remove":
        return "Removing offline table";
      default:
        return `Offline table: ${i.operation || "unknown"}`;
    }
  },

  // auth disabled — AuthMCP closed
  // auth: (i) => {
  //   switch (i.operation) {
  //     case "login":
  //       return "Logging in";
  //     case "refresh_token":
  //       return "Refreshing token";
  //     case "logout":
  //       return "Logging out";
  //     case "get_profile":
  //       return "Reading profile";
  //     default:
  //       return `Auth: ${i.operation || "unknown"}`;
  //   }
  // },

  common: (i) => {
    switch (i.operation) {
      case "generate_guids":
        return "Generating GUID";
      case "get_all_actions":
        return "Listing action names";
      case "get_action_detail":
        return "Reading action definition";
      case "check_existing_names":
        return "Checking existing action names";
      case "find_action_by_name":
        return i.name ? `Finding action '${i.name}'` : "Finding action";
      default:
        return humanizeOperation(i.operation);
    }
  },

  // Screen tools (additional)
  screen_get_all_names: () => "Listing all screens",

  // Project management tools
  ui_project_create_folder: (i) => (i.folderName ? `Creating '${i.folderName}' folder` : "Creating folder"),
  ui_project_rename_folder: () => "Renaming folder",
  ui_project_delete_folder: () => "Deleting folder",
  ui_project_add_item_to_folder: () => "Moving item to folder",
  ui_project_remove_item_from_folder: () => "Removing item from folder",
  ui_project_create_subfolder: () => "Creating subfolder",
  ui_project_rename_subfolder: () => "Renaming subfolder",
  ui_project_delete_subfolder: () => "Deleting subfolder",
  ui_project_add_item_to_subfolder: () => "Moving item to subfolder",
  ui_project_remove_item_from_subfolder: () => "Removing item from subfolder",

  // Role tools
  ui_project_list_roles: () => "Listing roles",
  ui_project_create_role: (i) => (i.roleName ? `Creating '${i.roleName}' role` : "Creating role"),
  ui_project_update_role: () => "Updating role",
  ui_project_delete_role: () => "Deleting role",
  ui_project_get_screen_authorization: () => "Reading selection authorization",
  ui_project_set_screen_authorization: () => "Setting selection authorization",

  // Comment tools
  ui_project_list_comments: () => "Listing comments",
  ui_project_post_comment: () => "Posting comment",
  ui_project_reply_to_comment: () => "Replying to comment",

  // Image tools
  ui_project_list_images: () => "Listing images",
  ui_project_upload_image: () => "Uploading image",

  // Workflow tools
  ui_project_list_workflows: () => "Listing workflows",
  ui_project_navigate_to_workflow: () => "Opening workflow",

  // Scheduled job navigation
  ui_project_list_scheduled_jobs: () => "Listing scheduled jobs",
  ui_project_navigate_to_scheduled_job: () => "Opening scheduled job",

  // Export tools
  ui_common_export_excel_template: () => "Exporting Excel template",
  ui_common_export_translations: () => "Exporting translations",

  // Validation input tools
  ui_common_list_validations: (i) => `Listing validations of ${resolveName(i.kuikaId) || "component"}`,
  ui_common_add_validation: (i) => {
    const name = resolveName(i.kuikaId);
    const type = i.validationType || "validation";
    return name ? `Adding ${type} validation to ${name}` : `Adding ${type} validation`;
  },
  ui_common_update_validation: (i) => `Updating validation on ${resolveName(i.kuikaId) || "component"}`,
  ui_common_remove_validation: (i) => `Removing validation from ${resolveName(i.kuikaId) || "component"}`,

  // RAG / documentation tool
  search_documentation: (i) => (i.query ? `Searching docs for '${i.query}'` : "Searching documentation"),

  // Plan progress tool
  plan_progress: (i) => {
    // tool_activity events wrap input as { toolName, parameters: {...}, ... }
    const p = i.parameters && typeof i.parameters === "object" ? i.parameters : i;
    switch (p.operation) {
      case "check":
        return p.stepIndex != null ? `Step ${p.stepIndex} completed` : "Updating plan progress";
      case "check_multiple": {
        const indices = Array.isArray(p.stepIndices) ? p.stepIndices : [];
        return indices.length > 0 ? `Steps ${indices.join(", ")} completed` : "Updating plan progress";
      }
      case "get_status":
        return "Checking plan status";
      case "abandon":
        return "Plan abandoned";
      default:
        return "Plan progress";
    }
  }
};

// Category tools: map toolName → set of read-only operations ("all" = entirely read-only)
const CATEGORY_READ_ONLY: Record<string, Set<string> | "all"> = {
  ui_screen_query: "all",
  ui_component: new Set(["select"]),
  ui_style: new Set(["get_definition", "get_defaults", "review", "screenshot", "list_colors"]),
  ui_property: new Set(["list_binding_options", "get_data_editor", "get_dynamic_types"]),
  ui_action: new Set([
    "get_parameter_options",
    "get_parameter_editor_options",
    "list_parameter_binding_options",
    "list_screen_actions",
    "list_current_actions",
    "get_details",
    "export_clipboard"
  ]),
  ui_screen: new Set(["list", "get_current"]),
  ui_project: new Set([
    "get_current_view",
    "list_datasources",
    "get_datasource_actions",
    "list_devices",
    "get_current_device",
    "export_translations",
    "export_excel_template"
  ]),
  ui_project_extended: new Set([
    "list_comments",
    "list_workflows",
    "list_scheduled_jobs",
    "list_apis",
    "get_api_details",
    "list_images",
    "list_roles",
    "get_screen_authorization"
  ]),
  ui_screen_input: new Set(["list"]),
  ui_validation: new Set(["list", "validate_screen"]),
  ui_microapp: new Set(["list_projects", "list_screens", "list_screen_inputs"]),
  ui_history: new Set([]), // undo/redo are never read-only

  // Backend grouped tools
  ds_datasource: new Set(["get_all", "get_detail"]),
  ds_sap: new Set(["test_connection", "search_functions", "get_function_signature"]),
  ds_csharp: new Set(["get_template"]),
  ds_action: new Set([]), // delete is never read-only
  database_schema: new Set(["get_schema_name", "get_tables", "get_table_schema", "get_column_types"]),
  db_sql_action: new Set(["test_connection", "get_parameter_types"]),
  database_data: new Set(["list_seedable"]),
  common: new Set(["generate_guids", "get_all_actions", "get_action_detail", "check_existing_names"]),
  translation: new Set(["get_supported_languages", "get_project_languages", "get_all"]),
  scheduler_job: new Set(["get_all", "get_detail"]),
  api_management: new Set(["get_all_controllers", "get_apis", "get_api_detail", "find_api_by_name"]),
  package_management: new Set([
    "get_available",
    "search_nuget",
    "get_nuget_details",
    "get_libraries",
    "get_library_detail"
  ]),
  lsp_csharp: "all",
  offline_table: new Set(["get_all", "get_detail"])
  // auth: new Set(["get_profile"]) // disabled — AuthMCP closed
};

// Backend tools that remain as flat (non-grouped) read-only tools
const BACKEND_READ_ONLY_TOOLS = new Set(["screen_get_all_names"]);

/**
 * Turn a raw snake_case operation into a readable sentence-case phrase, e.g.
 * `find_by_type` → "Find by type". Used as the universal fallback inside the
 * per-tool description switches so an unmapped (or newly-added) operation still
 * reads as plain words instead of leaking raw snake_case to the user.
 */
function humanizeOperation(operation: string | undefined): string {
  const spaced = String(operation ?? "")
    .replace(/_/g, " ")
    .trim();
  if (!spaced) return "Working";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Strip common prefixes and produce a readable label for unmapped tool names. */
function humanizeToolName(name: string): string {
  // Strip known prefixes
  let cleaned = name
    .replace(/^ui_(common|structure|style|logic|project|run)_/, "")
    .replace(
      /^(ds_|database_schema|database_data|db_sql_action|common|crud_|auth|translation|scheduler_job|api_management|package_management|lsp_csharp|offline_table|screen_)/,
      ""
    );
  // Replace underscores and capitalize
  cleaned = cleaned.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return cleaned || name;
}

export function getToolDescription(toolName: string, input?: ToolInput): string {
  const fn = TOOL_DESCRIPTIONS[toolName];
  if (fn) {
    // Stream-dispatched tools have input as { toolName, parameters, result, status }
    // where parameters holds the actual tool arguments. Extract them for richer descriptions.
    let resolvedInput = input || {};
    if (resolvedInput.parameters && !resolvedInput.kuikaId) {
      if (typeof resolvedInput.parameters === "string") {
        try {
          resolvedInput = JSON.parse(resolvedInput.parameters);
        } catch {
          // use as-is
        }
      } else if (typeof resolvedInput.parameters === "object") {
        resolvedInput = resolvedInput.parameters;
      }
    }
    return fn(resolvedInput);
  }
  return humanizeToolName(toolName);
}

export function isReadOnlyTool(toolName: string, input?: ToolInput): boolean {
  // Check category tools
  const catRO = CATEGORY_READ_ONLY[toolName];
  if (catRO !== undefined) {
    if (catRO === "all") return true;
    return input?.operation ? catRO.has(input.operation) : false;
  }
  // Check backend tools
  return BACKEND_READ_ONLY_TOOLS.has(toolName);
}

export interface InterruptDescriptionParts {
  /** Action text shown before the target name, e.g. "Delete component" */
  action: string;
  /** Human-readable target name, e.g. "Panel1". Rendered as a clickable link when targetKuikaId is set. */
  targetName: string;
  /** If set, clicking the target name selects this component on the canvas. */
  targetKuikaId: string | null;
  /** Structured detail items for migration confirmations */
  detailItems?: Array<{ label: string; subtitle?: string; columns?: string[]; isDrop?: boolean }>;
}

/** Build structured description for database migration interrupts. */
function buildMigrationDescription(
  input: ToolInput
): Pick<InterruptDescriptionParts, "action" | "targetName" | "detailItems"> {
  const tables: any[] = input.schema?.tables;
  if (!Array.isArray(tables) || tables.length === 0) {
    return { action: "Execute database migration", targetName: "" };
  }

  const detailItems: Array<{ label: string; subtitle?: string; columns?: string[]; isDrop?: boolean }> = [];
  let createdCount = 0;
  let droppedCount = 0;

  for (const t of tables) {
    const name = t.Name || t.name || "unnamed";
    const rawCols: any[] = Array.isArray(t.Columns || t.columns) ? t.Columns || t.columns : [];

    if (t.Dropped || t.dropped) {
      droppedCount++;
      detailItems.push({ label: name, isDrop: true });
    } else {
      createdCount++;
      const activeCols = rawCols.filter((c: any) => !c.Dropped && !c.dropped);
      const colNames = activeCols.map((c: any) => c.Name || c.name).filter(Boolean);
      detailItems.push({
        label: name,
        subtitle: `${colNames.length} column${colNames.length !== 1 ? "s" : ""}`,
        columns: colNames
      });
    }
  }

  let action: string;
  if (createdCount > 0 && droppedCount === 0) {
    action = `Create ${createdCount} table${createdCount > 1 ? "s" : ""}`;
  } else if (droppedCount > 0 && createdCount === 0) {
    action = `Drop ${droppedCount} table${droppedCount > 1 ? "s" : ""}`;
  } else {
    action = `Create ${createdCount} and drop ${droppedCount} table${droppedCount > 1 ? "s" : ""}`;
  }

  return { action, targetName: "", detailItems };
}

/**
 * Build a human-readable description for an interrupt confirmation prompt.
 * Returns structured parts so the UI can render the target as a clickable link.
 */
export function getInterruptDescription(toolName: string, input: ToolInput): string {
  const parts = getInterruptDescriptionParts(toolName, input);
  return parts.targetName ? `${parts.action} ${parts.targetName}` : parts.action;
}

export function getInterruptDescriptionParts(toolName: string, input: ToolInput): InterruptDescriptionParts {
  // Category tools — dispatch by toolName + operation
  if (toolName === "ui_component" && input.operation === "delete") {
    // delete uses kuikaIds[] (array), not kuikaId (singular)
    const ids = Array.isArray(input.kuikaIds) ? input.kuikaIds : input.kuikaId ? [input.kuikaId] : [];
    const names = ids
      .slice(0, 3)
      .map((id: string | number) => resolveName(id))
      .filter(Boolean);
    const firstId = ids.length > 0 ? String(ids[0]) : null;
    if (ids.length === 1) {
      return {
        action: "Delete component",
        targetName: names[0] || `#${firstId}`,
        targetKuikaId: firstId
      };
    }
    const suffix = ids.length > 3 ? ` +${ids.length - 3} more` : "";
    return {
      action: `Delete ${ids.length} components`,
      targetName: names.length > 0 ? names.join(", ") + suffix : "",
      targetKuikaId: firstId
    };
  }
  if (toolName === "ui_screen" && input.operation === "delete") {
    return {
      action: "Delete screen",
      targetName: String(input.screenName || input.screenId || ""),
      targetKuikaId: null
    };
  }
  if (toolName === "ui_project_extended") {
    switch (input.operation) {
      case "delete_role":
        return { action: "Delete role", targetName: String(input.name || input.roleId || ""), targetKuikaId: null };
      case "delete_folder":
        return { action: `Delete ${input.folderType || ""} folder`, targetName: "", targetKuikaId: null };
      case "delete_subfolder":
        return { action: "Delete subfolder", targetName: "", targetKuikaId: null };
    }
  }

  // Backend grouped tools — dispatch by toolName + operation
  if (toolName === "ds_datasource" && input.operation === "delete") {
    return {
      action: "Delete datasource",
      targetName: String(input.datasourceName || input.datasourceId || ""),
      targetKuikaId: null
    };
  }
  if (toolName === "ds_action" && input.operation === "delete") {
    return {
      action: "Delete datasource action",
      targetName: String(input.actionName || input.actionId || ""),
      targetKuikaId: null
    };
  }
  if (toolName === "database_schema" && input.operation === "execute_migration") {
    const migr = buildMigrationDescription(input);
    return { action: migr.action, targetName: migr.targetName, targetKuikaId: null, detailItems: migr.detailItems };
  }
  if (toolName === "translation") {
    switch (input.operation) {
      case "delete_all":
        return { action: "Delete all translations", targetName: "", targetKuikaId: null };
      case "delete_screen":
        return {
          action: "Delete screen translations",
          targetName: input.screenName ? `for '${input.screenName}'` : "",
          targetKuikaId: null
        };
    }
  }
  if (toolName === "api_management") {
    switch (input.operation) {
      case "delete_controller":
        return {
          action: "Delete API controller",
          targetName: String(input.controllerName || input.controllerId || ""),
          targetKuikaId: null
        };
      case "remove_api":
        return {
          action: "Remove API from controller",
          targetName: String(input.controllerName || input.controllerId || ""),
          targetKuikaId: null
        };
    }
  }
  if (toolName === "scheduler_job" && input.operation === "delete") {
    return {
      action: "Delete scheduled job",
      targetName: String(input.jobName || input.jobId || ""),
      targetKuikaId: null
    };
  }

  // Standalone tools
  switch (toolName) {
    case "ui_common_ask_user_question":
      return { action: "Clarifying questions", targetName: "", targetKuikaId: null };
    default:
      return { action: getToolDescription(toolName, input), targetName: "", targetKuikaId: null };
  }
}
